import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Tabs,
  Tab,
  Button,
  Grid,
  Tooltip,
  ThemeProvider,
  createTheme,
  AppBar,
  Toolbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  History as HistoryIcon,
} from '@mui/icons-material';

import documentEditorService from '../../services/documentEditor.service';
import { useProject } from '../../context/ProjectContext';
import { colors as dizzarooColors } from '../../theme/tokens';

// Dizzaroo brand theme – Primary: Deep Blue #1E73BE
const theme = createTheme({
  palette: {
    primary: {
      main: dizzarooColors.primary[500],
      light: dizzarooColors.primary.light,
      dark: dizzarooColors.primary[600],
      contrastText: '#ffffff',
    },
    secondary: {
      main: dizzarooColors.accent.teal,
      contrastText: '#ffffff',
    },
    background: { default: '#f5f5f5', paper: '#ffffff' },
  },
  typography: { fontFamily: '"Urbanist", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif' },
  components: {
    MuiButton: {
      defaultProps: { color: 'primary', disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, borderRadius: 8 },
        contained: {
          '&.MuiButton-colorPrimary': {
            backgroundColor: dizzarooColors.primary[500],
            '&:hover': { backgroundColor: dizzarooColors.primary[600] },
          },
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { boxShadow: '0 2px 4px rgba(0,0,0,0.05)' } } },
  },
});

const VersionHistory = () => {
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const projectIdFromUrl = queryParams.get('projectId');

  const { currentProject, loading: projectContextLoading, error: projectContextError, loadProject } = useProject();

  const actualProjectId = currentProject?._id || projectIdFromUrl;

  const [versionHistory, setVersionHistory] = useState([]);
  const [sectionTitle, setSectionTitle] = useState('');
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reverting, setReverting] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (projectIdFromUrl && (!currentProject || currentProject._id !== projectIdFromUrl)) {
      loadProject(projectIdFromUrl);
    }
  }, [projectIdFromUrl, currentProject, loadProject]);
  
  useEffect(() => {
    if (sectionId && actualProjectId && !projectContextLoading) {
      fetchVersionHistory();
    } else if (!sectionId && !actualProjectId && !projectContextLoading) {
      setError('No section ID or project selected.');
      setLoading(false);
    } else if (sectionId && !actualProjectId && !projectContextLoading) {
      setError('Please select a project to view its version history.');
      setLoading(false);
    }
  }, [sectionId, actualProjectId, projectContextLoading]);
  
  const fetchVersionHistory = async () => {
    try {
      setLoading(true);
      const response = await documentEditorService.getAllVersions(sectionId, actualProjectId);
      
      if (response.success && response.data) {
        setSectionTitle(response.data.title || 'Untitled Section');
        
        if (response.data.versions && response.data.versions.length > 0) {
          setVersionHistory(response.data.versions);
          // Select the latest version (highest versionNumber)
          const latestVersion = [...response.data.versions].sort((a, b) => 
            b.versionNumber - a.versionNumber)[0];
          setSelectedVersion(latestVersion);
        } else {
          setVersionHistory([]);
        }
      } else {
        setVersionHistory([]);
        setError('No version history available for this section.');
      }
    } catch (err) {
      console.error("Failed to load version history:", err);
      setVersionHistory([]);
      setError('Failed to load version history. Please check server and data.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleBack = () => {
    navigate(`/document-editor/protocol/${sectionId}?projectId=${actualProjectId}`);
  };
  
  const handleVersionSelect = (version) => {
    setSelectedVersion(version);
  };
  
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };
  
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };
  
  const handleRevertClick = () => {
    setConfirmDialogOpen(true);
  };
  
  const handleRevertConfirm = async () => {
    setConfirmDialogOpen(false);
    if (!selectedVersion) return;
    
    try {
      setReverting(true);
      
      // Create new version with reverted content
      await documentEditorService.addNewVersion({
        sectionId,
        title: sectionTitle,
        context: selectedVersion.context,
        prompt: selectedVersion.prompt,
        output: selectedVersion.output,
        projectId: actualProjectId,
        documentType: 'PROTOCOL'
      }, actualProjectId);
      
      // Update the section with the selected version's content
      await documentEditorService.updateSection(sectionId, {
        output: selectedVersion.output,
        prompt: '',
        context: selectedVersion.context
      }, actualProjectId);
      
      // Navigate back to editor with success indication
      navigate(`/document-editor/protocol/${sectionId}?projectId=${actualProjectId}`, { 
        state: { success: true, message: 'Section reverted successfully!' } 
      });
    } catch (error) {
      console.error("Failed to revert to version:", error);
      alert('Failed to revert section version. Please try again.');
    } finally {
      setReverting(false);
    }
  };
  
  const handleRevertCancel = () => {
    setConfirmDialogOpen(false);
  };
  
  // Loading state
  if (loading || projectContextLoading) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ height: '100%', bgcolor: 'background.default', minHeight: '100vh' }}>
          <AppBar position="static" color="default" elevation={2}>
            <Toolbar sx={{ minHeight: 64 }}>
              <Tooltip title="Back to Editor">
                <IconButton 
                  edge="start" 
                  color="inherit" 
                  onClick={handleBack} 
                  sx={{ mr: 2 }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
              <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 500 }}>
                Version History
              </Typography>
            </Toolbar>
          </AppBar>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
            <CircularProgress />
          </Box>
        </Box>
      </ThemeProvider>
    );
  }
  
  // Error state
  if (error || projectContextError) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ height: '100%', bgcolor: 'background.default', minHeight: '100vh' }}>
          <AppBar position="static" color="default" elevation={2}>
            <Toolbar sx={{ minHeight: 64 }}>
              <Tooltip title="Back to Editor">
                <IconButton 
                  edge="start" 
                  color="inherit" 
                  onClick={handleBack} 
                  sx={{ mr: 2 }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
              <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 500 }}>
                Version History
              </Typography>
            </Toolbar>
          </AppBar>
          
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
            <Paper sx={{ p: 4, maxWidth: 600, textAlign: 'center' }}>
              <Alert severity="error" sx={{ mb: 2 }}>{error || projectContextError}</Alert>
              <Button 
                variant="contained" 
                startIcon={<ArrowBackIcon />} 
                onClick={handleBack}
              >
                Return to Editor
              </Button>
            </Paper>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }
  
  // No history available
  if (versionHistory.length === 0) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ height: '100%', bgcolor: 'background.default', minHeight: '100vh' }}>
          <AppBar position="static" color="default" elevation={2}>
            <Toolbar sx={{ minHeight: 64 }}>
              <Tooltip title="Back to Editor">
                <IconButton 
                  edge="start" 
                  color="inherit" 
                  onClick={handleBack} 
                  sx={{ mr: 2 }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
              <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 500 }}>
                Version History
              </Typography>
            </Toolbar>
          </AppBar>
          
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
            <Paper sx={{ p: 4, maxWidth: 600, textAlign: 'center' }}>
              <HistoryIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                No Version History Available
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                No versions have been generated for this section yet. Generate content to create version history.
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<ArrowBackIcon />} 
                onClick={handleBack}
              >
                Return to Editor
              </Button>
            </Paper>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }
  
  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ height: '100%', bgcolor: 'background.default', minHeight: '100vh' }}>
        <AppBar position="static" color="default" elevation={2}>
          <Toolbar sx={{ minHeight: 64 }}>
            <Tooltip title="Back to Editor">
              <IconButton 
                edge="start" 
                color="inherit" 
                onClick={handleBack} 
                sx={{ mr: 2 }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 500 }}>
              Version History: {sectionTitle}
            </Typography>
            {selectedVersion && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleRevertClick}
                disabled={reverting}
                sx={{ ml: 2 }}
              >
                {reverting ? <CircularProgress size={24} /> : 'Revert to This Version'}
              </Button>
            )}
          </Toolbar>
        </AppBar>
        
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Paper sx={{ borderRadius: 2, height: '100%' }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ p: 2, borderBottom: '1px solid #eee', fontWeight: 500 }}
                >
                  Versions
                </Typography>
                <List sx={{ p: 0 }}>
                  {versionHistory.map((version, index) => (
                    <ListItem 
                      key={version.versionNumber || index} 
                      disablePadding 
                      divider={index < versionHistory.length - 1}
                    >
                      <ListItemButton 
                        selected={selectedVersion && selectedVersion.versionNumber === version.versionNumber}
                        onClick={() => handleVersionSelect(version)}
                        sx={{ 
                          '&.Mui-selected': { 
                            backgroundColor: 'primary.light',
                            color: 'primary.contrastText',
                          }
                        }}
                      >
                        <ListItemText 
                          primary={`Version ${version.versionNumber}`}
                          secondary={formatDate(version.createdAt)}
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={9}>
              {selectedVersion && (
                <Paper sx={{ borderRadius: 2, p: 0, overflow: 'hidden' }}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs 
                      value={currentTab} 
                      onChange={handleTabChange}
                      indicatorColor="primary"
                      textColor="primary"
                      variant="fullWidth"
                    >
                      <Tab label="Generated Content" />
                      <Tab label="Prompt" />
                    </Tabs>
                  </Box>
                  
                  <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {currentTab === 0 ? 'Generated Content' : 'Prompt'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Version {selectedVersion.versionNumber} • {formatDate(selectedVersion.createdAt)}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ whiteSpace: 'pre-wrap' }}>
                      {currentTab === 0 ? selectedVersion.output : selectedVersion.prompt}
                    </Box>
                  </Box>
                </Paper>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={handleRevertCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm Revert</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to revert to Version {selectedVersion?.versionNumber}? 
              This will create a new version with the content from the selected version.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleRevertCancel} color="inherit">
              Cancel
            </Button>
            <Button 
              onClick={handleRevertConfirm} 
              color="primary" 
              variant="contained"
              disabled={reverting}
            >
              {reverting ? <CircularProgress size={24} /> : 'Revert'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default VersionHistory;